import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, XIcon } from 'lucide-react';
import { useStore } from '../contexts/StoreContext';
import { Modal } from '../components/ui/Modal';
import { InquiryForm } from '../components/InquiryForm';
import { Vehicle } from '../types/catalog';
import { QuickQuote, amortizationLabel, getQuickQuote } from '../utils/finance';
import { formatARS, formatDate, formatPct } from '../utils/format';
import { vehicleLabel } from '../utils/catalog';
const NA = 'No disponible';
export function Compare() {
  const {
    vehicles,
    brands,
    compareIds,
    setCompareIds
  } = useStore();
  const [askFor, setAskFor] = useState<Vehicle | null>(null);
  const published = vehicles.filter(v => v.published);
  const selected = compareIds.map(id => published.find(v => v.id === id)).filter((v): v is Vehicle => !!v);
  const quotes = useMemo(() => selected.map(v => getQuickQuote(v, brands)), [selected, brands]);
  const setSlot = (i: number, id: string) => {
    const next = [...compareIds];
    if (id) next[i] = id;else next.splice(i, 1);
    setCompareIds(Array.from(new Set(next.filter(Boolean))));
  };
  const conditionsDiffer = (() => {
    const qs = quotes.filter((q): q is QuickQuote => !!q);
    if (qs.length < 2) return false;
    const key = (q: QuickQuote) => `${q.ctx.offer.tna}-${q.ctx.offer.amortization}-${q.term}-${q.ctx.offer.minDownPct}`;
    return new Set(qs.map(key)).size > 1;
  })();
  const rows: {
    label: string;
    get: (v: Vehicle, q: QuickQuote | null) => string;
  }[] = [{
    label: 'Precio de contado',
    get: v => formatARS(v.price)
  }, {
    label: 'Anticipo mínimo',
    get: (_, q) => q ? formatARS(q.down) : NA
  }, {
    label: 'Capital a financiar',
    get: (_, q) => q ? formatARS(q.principal) : NA
  }, {
    label: 'Cuota estimada',
    get: (_, q) => q?.sim ? `${q.term} × ${formatARS(q.sim.firstInstallment)}${q.ctx.offer.amortization === 'aleman' ? ' (1ª)' : ''}` : NA
  }, {
    label: 'Plazos habilitados',
    get: (_, q) => q && q.ctx.offer.terms.length ? `${q.ctx.offer.terms.join(' · ')} meses` : NA
  }, {
    label: 'Sistema',
    get: (_, q) => q ? amortizationLabel(q.ctx.offer.amortization) : NA
  }, {
    label: 'TNA',
    get: (_, q) => q ? formatPct(q.ctx.offer.tna, NA) : NA
  }, {
    label: 'CFT',
    get: (_, q) => q ? formatPct(q.ctx.offer.cft, NA) : NA
  }, {
    label: 'Estado de la condición',
    get: (_, q) => q ? q.ctx.offer.verified ? 'Oferta verificada' : 'Simulación referencial' : NA
  }, {
    label: 'Motor',
    get: v => v.engine ?? NA
  }, {
    label: 'Transmisión',
    get: v => v.transmission ?? NA
  }, {
    label: 'Consumo',
    get: v => v.consumption ?? NA
  }, {
    label: 'Seguridad',
    get: v => v.safety.length ? v.safety.join(' · ') : NA
  }, {
    label: 'Equipamiento',
    get: v => v.equipment.length ? v.equipment.join(' · ') : NA
  }, {
    label: 'Garantía',
    get: v => v.warranty ?? NA
  }, {
    label: 'Disponibilidad',
    get: v => v.availability
  }, {
    label: 'Datos actualizados',
    get: v => formatDate(v.updatedAt)
  }];
  const slots = [0, 1, 2];
  return <div className="container-x py-8 lg:py-12" data-id="element-1283" data-mp="3kpf43838ybr">
      <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl" data-id="element-1284" data-mp="3m0ii1xmg42c">Comparador de vehículos</h1>
      <p className="mt-2 max-w-2xl text-mist" data-id="element-1285" data-mp="ieu4m67efifz">Elegí hasta tres unidades, de la misma o de distintas marcas, y comparalas lado a lado.</p>

      <div className="mt-8 grid gap-3 sm:grid-cols-3" data-id="element-1286" data-mp="3lx3vkozl0ups">
        {slots.map(i => {
        const current = compareIds[i] ?? '';
        const disabled = i > compareIds.length;
        return <div key={i} data-id="element-1287" data-mp="3vorv7d6ali2c">
              <label htmlFor={`cmp-${i}`} className="label" data-id="element-1288" data-mp="3vp1bua5g6cvd">Vehículo {i + 1}</label>
              <div className="flex gap-2" data-id="element-1289" data-mp="3qw891rtjhelx">
                <select id={`cmp-${i}`} className="input" value={current} disabled={disabled} onChange={e => setSlot(i, e.target.value)} data-id="element-1290" data-mp="337fiybzlm0z8">
                  <option value="" data-id="element-1291" data-mp="2y79o6my17ycx">{disabled ? 'Completá el anterior' : 'Elegí una unidad'}</option>
                  {published.filter(v => v.id === current || !compareIds.includes(v.id)).map(v => <option key={v.id} value={v.id} data-id="element-1292" data-mp="25israigfnblo">{vehicleLabel(v, brands)}</option>)}
                </select>
                {current && <button onClick={() => setSlot(i, '')} className="btn btn-ghost w-11 px-0" aria-label="Quitar vehículo" data-id="element-1293" data-mp="2f82kk1n0j0bd">
                    <XIcon className="h-4 w-4" data-id="element-1294" data-mp="2k88iu0nsfl2f" />
                  </button>}
              </div>
            </div>;
      })}
      </div>

      {conditionsDiffer && <div className="mt-6 flex gap-3 rounded-xl border border-warn/30 bg-warn/5 p-4 text-sm text-warn" role="note" data-id="element-1295" data-mp="2t7viagwyigsu">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden data-id="element-1296" data-mp="2y5x1r2yaoi4u" />
          <p data-id="element-1297" data-mp="yuvxm9dpywep">
            Las cuotas se calculan con las condiciones propias de cada unidad (TNA, sistema, anticipo mínimo y plazo más largo). Como esas condiciones son
            distintas, las cuotas no son directamente comparables entre sí. Compará también el costo total en el simulador.
          </p>
        </div>}

      {selected.length === 0 ? <div className="mt-8 rounded-2xl border border-dashed border-line p-10 text-center" data-id="element-1298" data-mp="rh4rdxghbpt5">
          <p className="font-semibold" data-id="element-1299" data-mp="mhifbrce0flx">Todavía no elegiste vehículos</p>
          <p className="mt-1 text-sm text-mist" data-id="element-1300" data-mp="hmcwj1ea7h22">Usá los selectores de arriba o el botón “Comparar” en el catálogo.</p>
          <Link to="/catalogo" className="btn btn-primary mt-5" data-id="element-1301" data-mp="1f6fw1q0ya4ck">Ir al catálogo</Link>
        </div> : <div className="mt-8 overflow-x-auto rounded-2xl border border-line" data-id="element-1302" data-mp="1a7mjq0xegjdz">
          <table className="w-full min-w-[640px] border-collapse text-sm" data-id="element-1303" data-mp="158jd4c1ru53w">
            <caption className="sr-only" data-id="element-1304" data-mp="117zet55govg6">Comparación de vehículos seleccionados</caption>
            <thead data-id="element-1305" data-mp="3q1msdrrkvq1e">
              <tr className="bg-surface" data-id="element-1306" data-mp="3q24ydg41limi">
                <th scope="col" className="w-44 p-4 text-left align-bottom text-xs font-medium text-mist" data-id="element-1307" data-mp="3lyzxfhzm1ozz">Característica</th>
                {selected.map(v => <th key={v.id} scope="col" className="p-4 text-left align-top font-normal" data-id="element-1308" data-mp="3ngx4xyxo134p">
                    <img src={v.images[0]} alt="" className="aspect-[4/3] w-full rounded-lg object-cover" data-id="element-1309" data-mp="3segw1zfgpae2" />
                    <p className="mt-3 text-xs text-mist" data-id="element-1310" data-mp="3dhpqf8f732g0">{brands.find(b => b.id === v.brandId)?.name} · {v.year}</p>
                    <p className="font-display text-base font-bold" data-id="element-1311" data-mp="3iivlhcwbv1sp">{v.model}</p>
                    <p className="text-xs text-mist" data-id="element-1312" data-mp="34nkwgg4rlaw9">{v.version}</p>
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1313" data-mp="2urzyqu6qawsb">
              {rows.map(r => <tr key={r.label} data-id="element-1314" data-mp="2zqb8l4x0x7aj">
                  <th scope="row" className="bg-surface/60 p-4 text-left text-xs font-medium text-mist" data-id="element-1315" data-mp="2zrcnxll2ofy2">{r.label}</th>
                  {selected.map((v, i) => {
              const val = r.get(v, quotes[i]);
              return <td key={v.id} className={`p-4 align-top ${val === NA ? 'italic text-mist/70' : ''} ${r.label === 'Estado de la condición' && val === 'Oferta verificada' ? 'text-ok' : ''} ${r.label === 'Estado de la condición' && val === 'Simulación referencial' ? 'text-warn' : ''}`} data-id="element-1316" data-mp="27435mnkwo6y6" data-mpi={i}>
                        {val}
                      </td>;
            })}
                </tr>)}
              <tr data-id="element-1317" data-mp="2q25o0jiy75n0">
                <th scope="row" className="bg-surface/60 p-4" data-id="element-1318" data-mp="2q1nvnxzepbkd" />
                {selected.map(v => <td key={v.id} className="p-4" data-id="element-1319" data-mp="2l32u9v8f4luc">
                    <div className="grid gap-2" data-id="element-1320" data-mp="2yv96j68ga8oi">
                      <Link to={`/vehiculo/${v.id}`} className="btn btn-ghost" data-id="element-1321" data-mp="2yur4tu30r297">Ver detalle</Link>
                      <button onClick={() => setAskFor(v)} className="btn btn-primary" data-id="element-1322" data-mp="2uuyvvg9pqu3b">Solicitar asesoramiento</button>
                    </div>
                  </td>)}
              </tr>
            </tbody>
          </table>
        </div>}

      <Modal open={!!askFor} onClose={() => setAskFor(null)} title="Solicitar asesoramiento" data-id="element-1323" data-mp="f1ot4w4facfp">
        {askFor && <InquiryForm channel="comparador" vehicle={askFor} idPrefix="cmp" defaultMessage={`Estoy comparando: ${selected.map(v => vehicleLabel(v, brands)).join(' / ')}.`} data-id="element-1324" data-mp="j28s858k3q4h" />}
      </Modal>
    </div>;
}